import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Calendar } from "@repo/ui/components/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/select";
import { Switch } from "@repo/ui/components/switch";
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/tabs";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { CalendarClockIcon, ExternalLinkIcon, Loader2Icon } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_authed/settings/scheduling")({
  head: () => ({ meta: [{ title: "Scheduling settings" }] }),
  validateSearch: z.object({ date: z.string().optional() }),
  component: SchedulingSettings,
});

const durations = [5, 10, 15, 20, 30, 45, 60] as const;

type CalendarConfig = {
  dates: string[];
  startLocalTime: string;
  endLocalTime: string;
  dayHours?: Record<string, { startLocalTime: string; endLocalTime: string }>;
  photographers?: Record<string, string>;
  startIntervalMinutes: number;
  timezone: string;
};
type Config = {
  name: string;
  headshotLight: CalendarConfig & { durationMinutes: number };
  headshotDark: CalendarConfig & { durationMinutes: number };
  editorial: CalendarConfig & { soloDurationMinutes: number; teamDurationMinutes: number };
  publishedDates: string[];
};
type CalendarTab = "headshotLight" | "headshotDark" | "editorial";
const tabLabels: Record<CalendarTab, string> = {
  headshotLight: "Fullbody Light",
  headshotDark: "Portrait Dark",
  editorial: "Editorial",
};

const bookingTypes = [
  "headshot_light",
  "headshot_dark",
  "editorial_solo",
  "editorial_team",
] as const;
type BookingType = (typeof bookingTypes)[number];
const bookingTypeLabels: Record<BookingType, string> = {
  headshot_light: "Fullbody Light",
  headshot_dark: "Portrait Dark",
  editorial_solo: "Editorial Solo",
  editorial_team: "Editorial Team",
};

function SchedulingSettings() {
  const config = useQuery(api.scheduling.getConfig);
  const saveConfig = useMutation(api.scheduling.upsertConfig);
  const [draft, setDraft] = React.useState<Config>();
  const [tab, setTab] = React.useState<CalendarTab>("headshotLight");
  const { date: searchDate } = Route.useSearch();
  const [availabilityDay, setAvailabilityDay] = React.useState<string | undefined>(searchDate);
  const dayAvailability = useQuery(
    api.scheduling.getAdminDay,
    availabilityDay ? { date: availabilityDay } : "skip",
  );
  const setUnavailableType = useMutation(api.scheduling.setAdminUnavailableType);
  const publishSchedulingDays = useMutation(api.scheduling.publishSchedulingDays);
  const confirm = useConfirmationDialog();
  const [pendingType, setPendingType] = React.useState<BookingType | null>(null);
  const [publishingDate, setPublishingDate] = React.useState<string | null>(null);
  const value = draft ?? (config as Config | undefined);

  if (!value) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const updateCalendar = (nextCalendar: CalendarConfig) =>
    setDraft({ ...value, [tab]: { ...value[tab], ...nextCalendar } });
  const patchCalendar = <Key extends keyof CalendarConfig>(key: Key, next: CalendarConfig[Key]) =>
    updateCalendar({ ...value[tab], [key]: next });
  const setDayHours = (date: string, hours: { startLocalTime: string; endLocalTime: string }) =>
    setDraft({
      ...value,
      [tab]: { ...value[tab], dayHours: { ...value[tab].dayHours, [date]: hours } },
    });
  const resetDayHours = (date: string) => {
    const dayHours = { ...value[tab].dayHours };
    delete dayHours[date];
    setDraft({ ...value, [tab]: { ...value[tab], dayHours } });
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const saved = await saveConfig({
        config: {
          name: value.name,
          headshotLight: value.headshotLight,
          headshotDark: value.headshotDark,
          editorial: value.editorial,
        },
      });
      setDraft({ ...saved, publishedDates: value.publishedDates });
      toast.success("Scheduling settings saved.");
    } catch (error) {
      toast.error(error instanceof ConvexError ? String(error.data) : "Could not save settings.");
    }
  };
  const toggleAvailability = async (day: string, type: BookingType, disabled: boolean) => {
    if (disabled) {
      const accepted = await confirm({
        title: `Close ${bookingTypeLabels[type]} for this day?`,
        description:
          "This is a destructive change: nobody will be able to book this session type on this date. Existing bookings are not affected.",
        confirmText: "Close for the day",
        confirmButtonVariant: "destructive",
      });
      if (!accepted) return;
    }
    setPendingType(type);
    try {
      await setUnavailableType({ localDate: day, sessionType: type, disabled });
      toast.success(
        disabled
          ? `${bookingTypeLabels[type]} closed for this day.`
          : `${bookingTypeLabels[type]} enabled for this day.`,
      );
    } catch (error) {
      toast.error(
        error instanceof ConvexError ? String(error.data) : "Could not update availability.",
      );
    } finally {
      setPendingType(null);
    }
  };
  const toggleDayPublish = async (day: string, published: boolean) => {
    if (!published) {
      const accepted = await confirm({
        title: "Unpublish this day?",
        description:
          "The day will be hidden from the public calendar. Existing bookings are not affected.",
        confirmText: "Unpublish",
        confirmButtonVariant: "destructive",
      });
      if (!accepted) return;
    }
    setPublishingDate(day);
    try {
      const result = await publishSchedulingDays({ dates: [day], published });
      setDraft({ ...value, publishedDates: result.publishedDates });
      toast.success(published ? "Day published." : "Day unpublished.");
    } catch (error) {
      toast.error(
        error instanceof ConvexError ? String(error.data) : "Could not update publication status.",
      );
    } finally {
      setPublishingDate(null);
    }
  };

  const publishAllDrafts = async () => {
    const drafts = calendar.dates.filter((day) => !value.publishedDates.includes(day));
    if (!drafts.length) return;
    setPublishingDate("all");
    try {
      const result = await publishSchedulingDays({ dates: drafts, published: true });
      setDraft({ ...value, publishedDates: result.publishedDates });
      toast.success(`${drafts.length} day${drafts.length === 1 ? "" : "s"} published.`);
    } catch (error) {
      toast.error(
        error instanceof ConvexError ? String(error.data) : "Could not publish the days.",
      );
    } finally {
      setPublishingDate(null);
    }
  };
  const calendar = value[tab];

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <CalendarClockIcon className="size-5" /> Scheduling settings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure the independent Headshot and Editorial calendars.
          </p>
        </div>
        <Button
          variant="outline"
          render={<a href="/scheduling" target="_blank" rel="noreferrer" />}
        >
          Open public page <ExternalLinkIcon />
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Event</CardTitle>
          <CardDescription>This name appears on every public booking page.</CardDescription>
        </CardHeader>
        <CardContent>
          <Field>
            <FieldLabel htmlFor="schedule-name">Event name</FieldLabel>
            <Input
              id="schedule-name"
              value={value.name}
              onChange={(event) => setDraft({ ...value, name: event.target.value })}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Calendar availability</CardTitle>
          <CardDescription>
            Three independent studios. Editorial Solo and Editorial Team share the Editorial studio,
            so overlapping bookings are blocked.
          </CardDescription>
          <Tabs value={tab} onValueChange={(next) => setTab(next as CalendarTab)} className="mt-3">
            <TabsList>
              {(Object.keys(tabLabels) as CalendarTab[]).map((key) => (
                <TabsTrigger key={key} value={key}>
                  {tabLabels[key]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <form onSubmit={(event) => void submit(event)}>
            <FieldGroup>
              <Field>
                <FieldLabel>Available days</FieldLabel>
                <div className="rounded-lg border p-3">
                  <Calendar
                    mode="multiple"
                    defaultMonth={
                      calendar.dates[0] ? new Date(`${calendar.dates[0]}T00:00:00`) : new Date()
                    }
                    selected={calendar.dates.map((date) => new Date(`${date}T00:00:00`))}
                    onSelect={(nextDates) =>
                      patchCalendar(
                        "dates",
                        (nextDates ?? []).map((date) => date.toLocaleDateString("sv-SE")),
                      )
                    }
                    disabled={(date) => date < new Date(new Date().toDateString())}
                    className="mx-auto"
                  />
                </div>
                <FieldDescription>
                  {calendar.dates.length} day{calendar.dates.length === 1 ? "" : "s"} selected —
                  click a day to select or deselect it. New days start as drafts and are only
                  bookable after publishing.
                </FieldDescription>
                <div className="mt-2 space-y-1.5">
                  {[...calendar.dates].sort().map((day) => {
                    const isPublished = value.publishedDates.includes(day);
                    return (
                      <div key={day} className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium">
                          {new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                        <div className="flex items-center gap-2">
                          {isPublished ? (
                            <Badge className="border-emerald-500/40 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                              Published
                            </Badge>
                          ) : (
                            <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-600 dark:text-amber-400">
                              Draft
                            </Badge>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant={isPublished ? "outline" : "default"}
                            disabled={publishingDate === day}
                            onClick={() => void toggleDayPublish(day, !isPublished)}
                          >
                            {isPublished ? "Unpublish" : "Publish"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {calendar.dates.some((day) => !value.publishedDates.includes(day)) ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={publishingDate !== null}
                      onClick={() => void publishAllDrafts()}
                    >
                      Publish all drafts
                    </Button>
                  ) : null}
                </div>
              </Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field>
                  <FieldLabel htmlFor={`${tab}-start-time`}>Default from</FieldLabel>
                  <Input
                    id={`${tab}-start-time`}
                    type="time"
                    value={calendar.startLocalTime}
                    onChange={(event) => patchCalendar("startLocalTime", event.target.value)}
                    required
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`${tab}-end-time`}>Default to</FieldLabel>
                  <Input
                    id={`${tab}-end-time`}
                    type="time"
                    value={calendar.endLocalTime}
                    onChange={(event) => patchCalendar("endLocalTime", event.target.value)}
                    required
                  />
                </Field>
                <MinutesSelect
                  label="Start every"
                  value={calendar.startIntervalMinutes}
                  onChange={(next) => patchCalendar("startIntervalMinutes", next)}
                />
              </div>
              <Field>
                <FieldLabel>Daily hours</FieldLabel>
                <div className="max-h-72 overflow-y-auto rounded-lg border">
                  {[...calendar.dates].sort().map((date) => {
                    const hours = calendar.dayHours?.[date] ?? {
                      startLocalTime: calendar.startLocalTime,
                      endLocalTime: calendar.endLocalTime,
                    };
                    const isOverride = Boolean(calendar.dayHours?.[date]);
                    return (
                      <div
                        key={date}
                        className="flex items-center gap-2 border-b px-3 py-2 last:border-b-0"
                      >
                        <span className="w-32 shrink-0 text-sm font-medium">
                          {new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                        <Input
                          type="time"
                          className="w-32"
                          value={hours.startLocalTime}
                          onChange={(event) =>
                            setDayHours(date, { ...hours, startLocalTime: event.target.value })
                          }
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="time"
                          className="w-32"
                          value={hours.endLocalTime}
                          onChange={(event) =>
                            setDayHours(date, { ...hours, endLocalTime: event.target.value })
                          }
                        />
                        {isOverride ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => resetDayHours(date)}
                          >
                            Reset
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">default</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <FieldDescription>
                  Custom opening hours per day. Days without custom hours use the default From/To
                  above.
                </FieldDescription>
              </Field>
              {tab === "editorial" ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <MinutesSelect
                    label="Editorial Solo duration"
                    value={value.editorial.soloDurationMinutes}
                    onChange={(soloDurationMinutes) =>
                      setDraft({ ...value, editorial: { ...value.editorial, soloDurationMinutes } })
                    }
                  />
                  <MinutesSelect
                    label="Editorial Team duration"
                    value={value.editorial.teamDurationMinutes}
                    onChange={(teamDurationMinutes) =>
                      setDraft({ ...value, editorial: { ...value.editorial, teamDurationMinutes } })
                    }
                  />
                </div>
              ) : (
                <MinutesSelect
                  label="Headshot duration"
                  value={value[tab].durationMinutes}
                  onChange={(durationMinutes) =>
                    setDraft({ ...value, [tab]: { ...value[tab], durationMinutes } })
                  }
                />
              )}
              <Field>
                <FieldLabel htmlFor={`${tab}-timezone`}>Timezone</FieldLabel>
                <Input
                  id={`${tab}-timezone`}
                  value={calendar.timezone}
                  onChange={(event) => patchCalendar("timezone", event.target.value)}
                  required
                />
                <FieldDescription>Use an IANA timezone, for example Europe/Paris.</FieldDescription>
              </Field>
              <div className="flex justify-end border-t pt-5">
                <Button type="submit">Save settings</Button>
              </div>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Day availability</CardTitle>
          <CardDescription>
            Close session types for new bookings on a specific day. Disabling a type is destructive
            — existing bookings are never affected.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-[260px_1fr]">
          <div className="rounded-lg border p-3">
            <Calendar
              mode="single"
              selected={availabilityDay ? new Date(`${availabilityDay}T00:00:00`) : undefined}
              onSelect={(next) => next && setAvailabilityDay(next.toLocaleDateString("sv-SE"))}
              disabled={(next) => next < new Date(new Date().toDateString())}
              className="mx-auto"
            />
          </div>
          <div>
            {!availabilityDay ? (
              <p className="text-sm text-muted-foreground">
                Select a day on the left to manage its availability.
              </p>
            ) : (
              <div className="space-y-2">
                {bookingTypes.map((type) => {
                  const disabled = dayAvailability?.disabledTypes.includes(type) ?? false;
                  return (
                    <label
                      key={type}
                      className={cn(
                        "flex cursor-pointer items-center justify-between gap-3 rounded-md border p-3",
                        disabled && "bg-muted/40",
                      )}
                    >
                      <span
                        className={cn(
                          "text-sm font-medium",
                          disabled && "text-muted-foreground line-through",
                        )}
                      >
                        {bookingTypeLabels[type]}
                      </span>
                      <Switch
                        size="sm"
                        checked={!disabled}
                        disabled={pendingType === type}
                        onCheckedChange={(checked) =>
                          void toggleAvailability(availabilityDay, type, !checked)
                        }
                      />
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MinutesSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const id = label.toLowerCase().replaceAll(" ", "-");
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select value={String(value)} onValueChange={(next) => next && onChange(Number(next))}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {durations.map((duration) => (
            <SelectItem key={duration} value={String(duration)}>
              {duration} minutes
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
